/**
 * Cat Genetics Engine
 * Predicts litter outcomes based on feline genetics
 * 
 * Color Genetics Reference:
 * B = Black, b = chocolate, bl = cinnamon
 * D = Dense, d = dilute
 * O = Orange (sex-linked), o = not orange
 * A = Agouti (tabby), a = non-agouti (solid)
 * W = Dominant white, w = not white
 * S = White spotting, s = no spotting
 * C = Full color, cs = siamese, cb = burmese
 * L = Shorthair, l = longhair
 */

// ===================
// COLOR DEFINITIONS
// ===================

export const CAT_COLORS = {
  // Solid Colors
  black: { name: 'Black', hex: '#1a1a1a', genes: { B: 'B', D: 'D', O: 'oo' } },
  blue: { name: 'Blue', hex: '#6b7b8c', genes: { B: 'B', D: 'dd', O: 'oo' } },
  chocolate: { name: 'Chocolate', hex: '#5c3d2e', genes: { B: 'bb', D: 'D', O: 'oo' } },
  lilac: { name: 'Lilac', hex: '#b8a8b8', genes: { B: 'bb', D: 'dd', O: 'oo' } },
  cinnamon: { name: 'Cinnamon', hex: '#8b5a2b', genes: { B: 'blbl', D: 'D', O: 'oo' } },
  fawn: { name: 'Fawn', hex: '#d4b896', genes: { B: 'blbl', D: 'dd', O: 'oo' } },
  red: { name: 'Red/Orange', hex: '#e25822', genes: { O: 'OO' } },
  cream: { name: 'Cream', hex: '#f5deb3', genes: { O: 'OO', D: 'dd' } },
  white: { name: 'White', hex: '#ffffff', genes: { W: 'W' } },
  
  // Tortoiseshell (female only)
  tortie_black: { name: 'Black Tortie', hex: '#1a1a1a', pattern: 'tortie', genes: { B: 'B', D: 'D', O: 'Oo' }, femaleOnly: true },
  tortie_blue: { name: 'Blue Tortie (Blue-Cream)', hex: '#6b7b8c', pattern: 'tortie', genes: { B: 'B', D: 'dd', O: 'Oo' }, femaleOnly: true },
  tortie_chocolate: { name: 'Chocolate Tortie', hex: '#5c3d2e', pattern: 'tortie', genes: { B: 'bb', D: 'D', O: 'Oo' }, femaleOnly: true },
  tortie_lilac: { name: 'Lilac Tortie', hex: '#b8a8b8', pattern: 'tortie', genes: { B: 'bb', D: 'dd', O: 'Oo' }, femaleOnly: true },
  
  // Tabby patterns
  black_tabby: { name: 'Brown Tabby', hex: '#4a3728', pattern: 'tabby', genes: { B: 'B', D: 'D', A: 'A' } },
  blue_tabby: { name: 'Blue Tabby', hex: '#6b7b8c', pattern: 'tabby', genes: { B: 'B', D: 'dd', A: 'A' } },
  red_tabby: { name: 'Red Tabby', hex: '#e25822', pattern: 'tabby', genes: { O: 'OO', A: 'A' } },
  cream_tabby: { name: 'Cream Tabby', hex: '#f5deb3', pattern: 'tabby', genes: { O: 'OO', D: 'dd', A: 'A' } },
  silver_tabby: { name: 'Silver Tabby', hex: '#c0c0c0', pattern: 'tabby', genes: { B: 'B', I: 'I', A: 'A' } },
  
  // Pointed (Siamese pattern)
  seal_point: { name: 'Seal Point', hex: '#3d2b1f', pattern: 'pointed', genes: { B: 'B', D: 'D', C: 'cscs' } },
  blue_point: { name: 'Blue Point', hex: '#6b7b8c', pattern: 'pointed', genes: { B: 'B', D: 'dd', C: 'cscs' } },
  chocolate_point: { name: 'Chocolate Point', hex: '#5c3d2e', pattern: 'pointed', genes: { B: 'bb', D: 'D', C: 'cscs' } },
  lilac_point: { name: 'Lilac Point', hex: '#b8a8b8', pattern: 'pointed', genes: { B: 'bb', D: 'dd', C: 'cscs' } },
  flame_point: { name: 'Flame/Red Point', hex: '#e25822', pattern: 'pointed', genes: { O: 'OO', C: 'cscs' } },
  
  // Bicolor/With White
  black_white: { name: 'Black & White', hex: '#1a1a1a', pattern: 'bicolor', genes: { B: 'B', D: 'D', S: 'S' } },
  blue_white: { name: 'Blue & White', hex: '#6b7b8c', pattern: 'bicolor', genes: { B: 'B', D: 'dd', S: 'S' } },
  calico: { name: 'Calico', hex: '#ffffff', pattern: 'calico', genes: { O: 'Oo', S: 'S' }, femaleOnly: true },
  dilute_calico: { name: 'Dilute Calico', hex: '#ffffff', pattern: 'calico', genes: { O: 'Oo', S: 'S', D: 'dd' }, femaleOnly: true },
};

export const CAT_PATTERNS = {
  solid: { name: 'Solid', description: 'One uniform color' },
  tabby_mackerel: { name: 'Mackerel Tabby', description: 'Striped pattern' },
  tabby_classic: { name: 'Classic Tabby', description: 'Swirled/blotched pattern' },
  tabby_spotted: { name: 'Spotted Tabby', description: 'Spotted pattern' },
  tabby_ticked: { name: 'Ticked Tabby', description: 'Agouti/ticked pattern (Abyssinian)' },
  pointed: { name: 'Pointed', description: 'Color on extremities (Siamese)' },
  mink: { name: 'Mink', description: 'Between pointed and solid (Tonkinese)' },
  sepia: { name: 'Sepia', description: 'Burmese pattern' },
  bicolor: { name: 'Bicolor', description: 'With white patches' },
  van: { name: 'Van', description: 'Mostly white with color on head/tail' },
  harlequin: { name: 'Harlequin', description: 'High white with color patches' },
};

// ===================
// BREED DATA
// ===================

export const BREED_GENETICS = {
  'Persian': {
    avgLitterSize: 4,
    litterRange: [2, 6],
    commonColors: ['white', 'black', 'blue', 'cream', 'red', 'tortie_black', 'calico', 'silver_tabby'],
    healthRisks: [
      { condition: 'PKD (Polycystic Kidney Disease)', risk: 'high', testAvailable: true },
      { condition: 'HCM (Hypertrophic Cardiomyopathy)', risk: 'medium', testAvailable: true },
      { condition: 'PRA (Progressive Retinal Atrophy)', risk: 'medium', testAvailable: true },
      { condition: 'Brachycephalic issues', risk: 'high', testAvailable: false },
    ],
    geneticNotes: 'Longhair gene (ll) is fixed. Watch for flat-face breathing issues.'
  },
  'Maine Coon': {
    avgLitterSize: 5,
    litterRange: [4, 8],
    commonColors: ['black_tabby', 'red_tabby', 'silver_tabby', 'black', 'blue', 'cream', 'tortie_black'],
    healthRisks: [
      { condition: 'HCM (Hypertrophic Cardiomyopathy)', risk: 'high', testAvailable: true },
      { condition: 'SMA (Spinal Muscular Atrophy)', risk: 'medium', testAvailable: true },
      { condition: 'Hip Dysplasia', risk: 'medium', testAvailable: true },
      { condition: 'PKD', risk: 'low', testAvailable: true },
    ],
    geneticNotes: 'Large breed - monitor for joint issues. Polydactyl gene may be present.'
  },
  'Siamese': {
    avgLitterSize: 5,
    litterRange: [4, 6],
    commonColors: ['seal_point', 'blue_point', 'chocolate_point', 'lilac_point', 'flame_point'],
    healthRisks: [
      { condition: 'Amyloidosis', risk: 'medium', testAvailable: false },
      { condition: 'Asthma/Bronchial disease', risk: 'medium', testAvailable: false },
      { condition: 'PRA', risk: 'low', testAvailable: true },
      { condition: 'Convergent strabismus (crossed eyes)', risk: 'low', testAvailable: false },
    ],
    geneticNotes: 'Pointed pattern (cscs) is fixed. Temperature-sensitive albinism.'
  },
  'British Shorthair': {
    avgLitterSize: 4,
    litterRange: [3, 6],
    commonColors: ['blue', 'black', 'white', 'cream', 'lilac', 'chocolate', 'silver_tabby', 'blue_tabby'],
    healthRisks: [
      { condition: 'HCM', risk: 'high', testAvailable: true },
      { condition: 'PKD', risk: 'medium', testAvailable: true },
      { condition: 'Hemophilia B', risk: 'low', testAvailable: true },
    ],
    geneticNotes: 'Blue is most popular. Dense, plush coat. Prone to obesity.'
  },
  'Bengal': {
    avgLitterSize: 5,
    litterRange: [3, 6],
    commonColors: ['black_tabby', 'silver_tabby'],
    patterns: ['spotted', 'marble'],
    healthRisks: [
      { condition: 'PK Deficiency', risk: 'medium', testAvailable: true },
      { condition: 'PRA (Bengal form)', risk: 'medium', testAvailable: true },
      { condition: 'HCM', risk: 'medium', testAvailable: true },
      { condition: 'Flat Chested Kitten Syndrome', risk: 'low', testAvailable: false },
    ],
    geneticNotes: 'Glitter gene may be present. Spotted/rosetted pattern preferred.'
  },
  'Ragdoll': {
    avgLitterSize: 5,
    litterRange: [4, 6],
    commonColors: ['seal_point', 'blue_point', 'chocolate_point', 'lilac_point', 'flame_point'],
    patterns: ['colorpoint', 'mitted', 'bicolor'],
    healthRisks: [
      { condition: 'HCM', risk: 'high', testAvailable: true },
      { condition: 'PKD', risk: 'low', testAvailable: true },
      { condition: 'Mucopolysaccharidosis', risk: 'low', testAvailable: true },
    ],
    geneticNotes: 'Pointed pattern with variations in white spotting. Slow to mature.'
  },
  'Abyssinian': {
    avgLitterSize: 4,
    litterRange: [3, 5],
    commonColors: ['cinnamon', 'fawn', 'blue', 'black'],
    healthRisks: [
      { condition: 'PK Deficiency', risk: 'high', testAvailable: true },
      { condition: 'PRA', risk: 'high', testAvailable: true },
      { condition: 'Renal Amyloidosis', risk: 'medium', testAvailable: false },
    ],
    geneticNotes: 'Ticked tabby pattern is fixed. No stripes or spots on body.'
  },
  'Scottish Fold': {
    avgLitterSize: 4,
    litterRange: [2, 5],
    commonColors: ['blue', 'black', 'white', 'cream', 'silver_tabby', 'calico'],
    healthRisks: [
      { condition: 'Osteochondrodysplasia', risk: 'very high', testAvailable: false },
      { condition: 'HCM', risk: 'medium', testAvailable: true },
      { condition: 'PKD', risk: 'low', testAvailable: true },
    ],
    geneticNotes: '⚠️ NEVER breed Fold x Fold - causes severe cartilage issues. Always breed Fold x Straight.'
  },
  'Sphynx': {
    avgLitterSize: 4,
    litterRange: [3, 6],
    commonColors: ['black', 'blue', 'red', 'cream', 'white', 'tortie_black'],
    healthRisks: [
      { condition: 'HCM', risk: 'high', testAvailable: true },
      { condition: 'Hereditary Myopathy', risk: 'medium', testAvailable: true },
      { condition: 'Skin conditions', risk: 'medium', testAvailable: false },
    ],
    geneticNotes: 'Hairless gene (hr) is recessive. Need temperature-controlled environment.'
  },
  'Russian Blue': {
    avgLitterSize: 4,
    litterRange: [3, 5],
    commonColors: ['blue'],
    healthRisks: [
      { condition: 'Bladder stones', risk: 'low', testAvailable: false },
    ],
    geneticNotes: 'Only comes in blue. Double coat with silver tipping. Generally very healthy breed.'
  },
  'Burmese': {
    avgLitterSize: 5,
    litterRange: [4, 6],
    commonColors: ['chocolate', 'blue', 'lilac', 'cream', 'red', 'tortie_chocolate'],
    healthRisks: [
      { condition: 'Burmese Head Defect', risk: 'medium', testAvailable: true },
      { condition: 'Hypokalemia', risk: 'medium', testAvailable: true },
      { condition: 'Diabetes', risk: 'medium', testAvailable: false },
      { condition: 'Flat Chested Kitten Syndrome', risk: 'low', testAvailable: false },
    ],
    geneticNotes: 'Sepia pattern (cbcb) is fixed. Contemporary vs Traditional types.'
  },
  'Norwegian Forest Cat': {
    avgLitterSize: 5,
    litterRange: [4, 6],
    commonColors: ['black_tabby', 'red_tabby', 'black', 'blue', 'cream', 'white', 'tortie_black'],
    healthRisks: [
      { condition: 'GSD IV (Glycogen Storage Disease)', risk: 'high', testAvailable: true },
      { condition: 'HCM', risk: 'medium', testAvailable: true },
      { condition: 'Hip Dysplasia', risk: 'low', testAvailable: true },
    ],
    geneticNotes: 'Semi-longhair with water-resistant coat. Large breed, slow maturing.'
  },
  'Exotic Shorthair': {
    avgLitterSize: 4,
    litterRange: [2, 5],
    commonColors: ['white', 'black', 'blue', 'cream', 'red', 'silver_tabby', 'calico'],
    healthRisks: [
      { condition: 'PKD', risk: 'high', testAvailable: true },
      { condition: 'HCM', risk: 'medium', testAvailable: true },
      { condition: 'Brachycephalic issues', risk: 'high', testAvailable: false },
      { condition: 'PRA', risk: 'medium', testAvailable: true },
    ],
    geneticNotes: 'Shorthaired Persian. Same health concerns. May carry longhair gene.'
  },
  'Birman': {
    avgLitterSize: 4,
    litterRange: [3, 5],
    commonColors: ['seal_point', 'blue_point', 'chocolate_point', 'lilac_point'],
    healthRisks: [
      { condition: 'HCM', risk: 'medium', testAvailable: true },
      { condition: 'Feline Audiogenic Reflex Seizures', risk: 'low', testAvailable: false },
    ],
    geneticNotes: 'White gloves/laces on all four paws. Pointed with white spotting gene.'
  },
  'Other': {
    avgLitterSize: 4,
    litterRange: [2, 8],
    commonColors: Object.keys(CAT_COLORS),
    healthRisks: [],
    geneticNotes: 'Mixed breed or unlisted breed. General feline genetics apply.'
  }
};

// ===================
// GENETICS CALCULATOR
// ===================

/**
 * Simple Punnett Square calculation
 * @param {string} parent1Gene - e.g., "Bb" 
 * @param {string} parent2Gene - e.g., "Bb"
 * @returns {Object} - Probability of each outcome
 */
export const punnettSquare = (parent1Gene, parent2Gene) => {
  if (!parent1Gene || !parent2Gene || parent1Gene.length !== 2 || parent2Gene.length !== 2) {
    return null;
  }
  
  const outcomes = {};
  const alleles1 = [parent1Gene[0], parent1Gene[1]];
  const alleles2 = [parent2Gene[0], parent2Gene[1]];
  
  for (const a1 of alleles1) {
    for (const a2 of alleles2) {
      // Sort to normalize (e.g., "bB" becomes "Bb")
      const genotype = [a1, a2].sort((x, y) => {
        if (x.toLowerCase() === y.toLowerCase()) return x < y ? -1 : 1;
        return x.toLowerCase() < y.toLowerCase() ? -1 : 1;
      }).join('');
      
      outcomes[genotype] = (outcomes[genotype] || 0) + 1;
    }
  }
  
  // Convert to percentages
  const total = 4;
  const probabilities = {};
  for (const [genotype, count] of Object.entries(outcomes)) {
    probabilities[genotype] = {
      count,
      percentage: (count / total) * 100,
      ratio: `${count}/4`
    };
  }
  
  return probabilities;
};

/**
 * Determine phenotype from genotype
 */
const getPhenotype = (genotype, gene) => {
  switch (gene) {
    case 'B': // Black/Chocolate/Cinnamon
      if (genotype.includes('B')) return 'black-based';
      if (genotype === 'bb') return 'chocolate-based';
      if (genotype === 'blbl') return 'cinnamon-based';
      return 'black-based';
    case 'D': // Dense/Dilute
      return genotype.includes('D') ? 'dense' : 'dilute';
    case 'O': // Orange (sex-linked)
      if (genotype === 'OO' || genotype === 'OY') return 'orange';
      if (genotype === 'Oo') return 'tortie';
      return 'not-orange';
    case 'A': // Agouti/Non-agouti
      return genotype.includes('A') ? 'tabby' : 'solid';
    case 'W': // White
      return genotype.includes('W') ? 'white' : 'not-white';
    case 'S': // White spotting
      return genotype.includes('S') ? 'with-white' : 'no-white';
    default:
      return 'unknown';
  }
};

/**
 * Main color prediction function
 */
export const predictKittenColors = (queen, sire) => {
  const predictions = [];
  
  // Simplified color crossing logic
  const queenColor = CAT_COLORS[queen.color];
  const sireColor = CAT_COLORS[sire.color];
  
  if (!queenColor || !sireColor) {
    return [{ color: 'unknown', probability: 100, note: 'Unable to determine - color data missing' }];
  }
  
  // Special case: White is dominant
  if (queen.color === 'white' || sire.color === 'white') {
    predictions.push({ color: 'white', probability: 50, hex: '#ffffff' });
  }
  
  // Orange gene is sex-linked - handle specially
  const queenHasOrange = queenColor.genes?.O?.includes('O');
  const sireHasOrange = sireColor.genes?.O?.includes('O');
  
  // Basic color inheritance
  const colorCombos = calculateColorCombinations(queenColor, sireColor);
  
  return colorCombos;
};

/**
 * Calculate color combinations from parent colors
 */
const calculateColorCombinations = (queenColor, sireColor) => {
  const results = [];
  
  // Get base colors
  const queenBase = getBaseColor(queenColor);
  const sireBase = getBaseColor(sireColor);
  
  // Simple inheritance model
  if (queenBase === sireBase) {
    // Same color parents - mostly same color offspring
    results.push({
      color: queenBase,
      probability: 75,
      hex: queenColor.hex,
      name: queenColor.name
    });
    results.push({
      color: 'variation',
      probability: 25,
      hex: '#888888',
      name: 'Color variation possible'
    });
  } else {
    // Different color parents
    results.push({
      color: queenBase,
      probability: 35,
      hex: queenColor.hex,
      name: queenColor.name
    });
    results.push({
      color: sireBase,
      probability: 35,
      hex: sireColor.hex,
      name: sireColor.name
    });
    results.push({
      color: 'mix',
      probability: 30,
      hex: '#888888',
      name: 'Mix/Carrier colors'
    });
  }
  
  // Handle tortie possibility (queen must carry orange)
  if (queenColor.genes?.O?.includes('O') || sireColor.genes?.O?.includes('O')) {
    results.push({
      color: 'tortie',
      probability: 25,
      hex: '#8b4513',
      name: 'Tortoiseshell (females only)',
      femaleOnly: true
    });
  }
  
  return results;
};

const getBaseColor = (colorData) => {
  if (!colorData) return 'unknown';
  
  // Determine base from genes
  if (colorData.genes?.W === 'W') return 'white';
  if (colorData.genes?.O === 'OO') return 'orange';
  if (colorData.genes?.B === 'bb') return 'chocolate';
  if (colorData.genes?.B === 'blbl') return 'cinnamon';
  if (colorData.genes?.D === 'dd') return 'dilute';
  return 'black';
};

// ===================
// LITTER SIZE PREDICTOR
// ===================

/**
 * Predict litter size based on multiple factors
 */
export const predictLitterSize = (breed, queenAge, previousLitters = [], isFirstLitter = false) => {
  const breedData = BREED_GENETICS[breed] || BREED_GENETICS['Other'];
  
  let baseAvg = breedData.avgLitterSize;
  let [minSize, maxSize] = breedData.litterRange;
  
  // Age factor
  let ageFactor = 1.0;
  if (queenAge < 1.5) {
    ageFactor = 0.7; // Young queens have smaller litters
    minSize = Math.max(1, minSize - 1);
  } else if (queenAge >= 1.5 && queenAge <= 5) {
    ageFactor = 1.1; // Prime age
    maxSize += 1;
  } else if (queenAge > 5 && queenAge <= 8) {
    ageFactor = 0.9; // Older queen
  } else if (queenAge > 8) {
    ageFactor = 0.7; // Senior queen
    maxSize -= 1;
  }
  
  // First litter factor
  if (isFirstLitter) {
    ageFactor *= 0.85;
    minSize = Math.max(1, minSize - 1);
  }
  
  // Previous litter history factor
  let historyFactor = 1.0;
  if (previousLitters.length > 0) {
    const avgPrevious = previousLitters.reduce((a, b) => a + b, 0) / previousLitters.length;
    historyFactor = avgPrevious / breedData.avgLitterSize;
    // Clamp between 0.7 and 1.3
    historyFactor = Math.max(0.7, Math.min(1.3, historyFactor));
  }
  
  const predictedAvg = Math.round(baseAvg * ageFactor * historyFactor * 10) / 10;
  
  return {
    predicted: Math.round(predictedAvg),
    range: [Math.max(1, minSize), maxSize],
    confidence: previousLitters.length > 2 ? 'high' : (previousLitters.length > 0 ? 'medium' : 'low'),
    factors: {
      breed: breedData.avgLitterSize,
      age: ageFactor > 1 ? 'favorable' : (ageFactor < 1 ? 'reduced' : 'normal'),
      history: previousLitters.length > 0 ? `Avg of ${previousLitters.length} previous: ${(previousLitters.reduce((a,b)=>a+b,0)/previousLitters.length).toFixed(1)}` : 'No history'
    }
  };
};

// ===================
// HEALTH RISK ANALYZER
// ===================

/**
 * Analyze health risks for a potential mating
 */
export const analyzeHealthRisks = (breed, queenHealth = {}, sireHealth = {}) => {
  const breedData = BREED_GENETICS[breed] || BREED_GENETICS['Other'];
  const risks = [];
  const warnings = [];
  const recommendations = [];
  
  // Add breed-specific risks
  for (const risk of breedData.healthRisks) {
    const riskEntry = {
      condition: risk.condition,
      severity: risk.risk,
      testAvailable: risk.testAvailable,
      inherited: true
    };
    
    // Check if parents are tested
    if (risk.testAvailable) {
      const queenTested = queenHealth[risk.condition]?.tested;
      const sireTested = sireHealth[risk.condition]?.tested;
      
      if (!queenTested || !sireTested) {
        recommendations.push(`Get both parents tested for ${risk.condition}`);
        riskEntry.status = 'unknown - testing recommended';
      } else {
        const queenClear = queenHealth[risk.condition]?.result === 'clear';
        const sireClear = sireHealth[risk.condition]?.result === 'clear';
        
        if (queenClear && sireClear) {
          riskEntry.status = 'both parents clear ✓';
          riskEntry.severity = 'very low';
        } else if (!queenClear && !sireClear) {
          riskEntry.status = 'BOTH PARENTS AFFECTED - HIGH RISK';
          riskEntry.severity = 'very high';
          warnings.push(`⚠️ Both parents affected for ${risk.condition} - DO NOT BREED`);
        } else {
          riskEntry.status = 'one parent carrier - 50% kittens may carry';
          riskEntry.severity = 'medium';
        }
      }
    }
    
    risks.push(riskEntry);
  }
  
  // Breed-specific warnings
  if (breed === 'Scottish Fold') {
    warnings.push('⚠️ NEVER breed Fold x Fold - causes severe bone/cartilage disease');
    recommendations.push('Always breed Fold x Straight Ear only');
  }
  
  // Age-related warnings
  if (queenHealth.age && queenHealth.age < 1) {
    warnings.push('⚠️ Queen is under 1 year - too young to breed safely');
  }
  if (queenHealth.age && queenHealth.age > 8) {
    warnings.push('⚠️ Queen is over 8 years - increased pregnancy risks');
    recommendations.push('Consider veterinary consultation before breeding');
  }
  
  // General recommendations
  if (recommendations.length === 0) {
    recommendations.push('Schedule pre-breeding vet check for queen');
    recommendations.push('Ensure vaccinations are up to date');
    recommendations.push('Plan for proper nutrition during pregnancy');
  }
  
  return {
    risks,
    warnings,
    recommendations,
    overallRisk: warnings.length > 0 ? 'high' : (risks.some(r => r.severity === 'high') ? 'medium' : 'low'),
    breedNotes: breedData.geneticNotes
  };
};

// ===================
// INBREEDING CALCULATOR
// ===================

/**
 * Calculate Coefficient of Inbreeding (COI)
 * Based on Wright's formula - simplified version
 */
export const calculateCOI = (queenPedigree, sirePedigree, generations = 4) => {
  // This is a simplified version
  // Full implementation would traverse pedigree tree
  
  if (!queenPedigree || !sirePedigree) {
    return {
      coi: null,
      status: 'unknown',
      note: 'Pedigree data required for COI calculation'
    };
  }
  
  // Find common ancestors
  const queenAncestors = flattenPedigree(queenPedigree, generations);
  const sireAncestors = flattenPedigree(sirePedigree, generations);
  
  const commonAncestors = queenAncestors.filter(a => 
    sireAncestors.some(s => s.id === a.id || s.name === a.name)
  );
  
  // Simplified COI calculation
  let coi = 0;
  for (const ancestor of commonAncestors) {
    // Each common ancestor contributes based on how far back they appear
    const contribution = Math.pow(0.5, ancestor.generation + 1);
    coi += contribution;
  }
  
  coi = Math.min(coi * 100, 50); // Cap at 50%
  
  let status = 'excellent';
  let recommendation = 'Low inbreeding - good genetic diversity';
  
  if (coi > 25) {
    status = 'dangerous';
    recommendation = '⚠️ Very high inbreeding - DO NOT BREED';
  } else if (coi > 12.5) {
    status = 'high';
    recommendation = 'High inbreeding - consider different mate';
  } else if (coi > 6.25) {
    status = 'moderate';
    recommendation = 'Moderate inbreeding - acceptable but monitor';
  } else if (coi > 3) {
    status = 'low';
    recommendation = 'Low inbreeding - acceptable';
  }
  
  return {
    coi: Math.round(coi * 100) / 100,
    status,
    recommendation,
    commonAncestors: commonAncestors.length,
    note: `Based on ${generations}-generation pedigree analysis`
  };
};

const flattenPedigree = (pedigree, maxGen, currentGen = 0) => {
  if (!pedigree || currentGen >= maxGen) return [];
  
  const ancestors = [{ ...pedigree, generation: currentGen }];
  
  if (pedigree.sire) {
    ancestors.push(...flattenPedigree(pedigree.sire, maxGen, currentGen + 1));
  }
  if (pedigree.dam) {
    ancestors.push(...flattenPedigree(pedigree.dam, maxGen, currentGen + 1));
  }
  
  return ancestors;
};

// ===================
// UTILITY EXPORTS
// ===================

export const getBreedList = () => Object.keys(BREED_GENETICS).sort();
export const getColorList = () => Object.entries(CAT_COLORS).map(([key, val]) => ({ id: key, ...val }));
export const getPatternList = () => Object.entries(CAT_PATTERNS).map(([key, val]) => ({ id: key, ...val }));
